import React from 'react';
import styled from 'styled-components';

const TableContainer = styled.div`
  overflow: hidden;
  border: 1px solid var(--border-primary);
  border-radius: var(--radius-lg);
  background: var(--background-secondary);
  box-shadow: var(--shadow-sm);
  
  @media (max-width: 768px) {
    overflow-x: auto;
  }
`;

const ScrollContainer = styled.div`
  overflow-x: auto;
  
  /* Custom scrollbar */
  &::-webkit-scrollbar {
    height: 6px;
  }
  
  &::-webkit-scrollbar-track {
    background: var(--background-tertiary);
  }
  
  &::-webkit-scrollbar-thumb {
    background: var(--border-secondary);
    border-radius: 3px;
  }
`;

const StyledTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  min-width: 600px;
`;

const TableHeader = styled.thead`
  background: linear-gradient(135deg, var(--background-tertiary) 0%, var(--gray-100) 100%);
`;

const TableHeaderCell = styled.th<{ sortable?: boolean }>`
  padding: 16px 20px;
  text-align: left;
  font-weight: 600;
  font-size: 13px;
  letter-spacing: 0.05em;
  text-transform: uppercase;
  color: var(--text-secondary);
  cursor: ${props => props.sortable ? 'pointer' : 'default'};
  user-select: none;
  border-bottom: 2px solid var(--border-primary);
  transition: all 0.2s ease;
  position: relative;
  
  &:hover {
    background: ${props => props.sortable ? 'var(--gray-200)' : 'transparent'};
    color: ${props => props.sortable ? 'var(--text-primary)' : 'var(--text-secondary)'};
  }
  
  &:first-child {
    border-top-left-radius: var(--radius-lg);
  }
  
  &:last-child {
    border-top-right-radius: var(--radius-lg);
  }
  
  @media (max-width: 768px) {
    padding: 12px 16px;
    font-size: 11px;
  }
`;

const TableBody = styled.tbody``;

const TableRow = styled.tr<{ clickable?: boolean }>`
  border-bottom: 1px solid var(--border-primary);
  cursor: ${props => props.clickable ? 'pointer' : 'default'};
  transition: all 0.2s ease;
  
  &:hover {
    background: ${props => props.clickable ? 'var(--primary-50)' : 'transparent'};
    transform: ${props => props.clickable ? 'translateY(-1px)' : 'none'};
  }
  
  &:last-child {
    border-bottom: none;
    
    td:first-child {
      border-bottom-left-radius: var(--radius-lg);
    }
    
    td:last-child {
      border-bottom-right-radius: var(--radius-lg);
    }
  }
`;

const TableCell = styled.td`
  padding: 16px 20px;
  color: var(--text-primary);
  font-size: 14px;
  line-height: 1.4;
  
  @media (max-width: 768px) {
    padding: 12px 16px;
    font-size: 13px;
  }
`;

const SortIcon = styled.span<{ direction?: 'asc' | 'desc' | null }>`
  margin-left: 6px;
  font-size: 14px;
  color: var(--accent-primary);
  transition: all 0.2s ease;
  
  &::after {
    content: ${props => 
      props.direction === 'asc' ? '"↑"' : 
      props.direction === 'desc' ? '"↓"' : 
      '"↕"'
    };
  }
`;

const LoadingContainer = styled.div`
  padding: 40px 20px;
  text-align: center;
  color: var(--text-secondary);
`;

const LoadingSpinner = styled.div`
  display: inline-block;
  width: 20px;
  height: 20px;
  border: 2px solid var(--border-secondary);
  border-radius: 50%;
  border-top-color: var(--accent-primary);
  animation: spin 0.8s ease-in-out infinite;
  margin-bottom: 12px;
`;

const EmptyState = styled.div`
  padding: 40px 20px;
  text-align: center;
  color: var(--text-tertiary);
  font-size: 14px;
`;

interface Column {
  key: string;
  title: string;
  sortable?: boolean;
  render?: (value: any, record: any) => React.ReactNode;
}

interface TableProps {
  columns: Column[];
  data: any[];
  loading?: boolean;
  sortBy?: string;
  sortDirection?: 'asc' | 'desc';
  onSort?: (key: string) => void;
  onRowClick?: (record: any) => void;
}

export const Table: React.FC<TableProps> = ({
  columns,
  data,
  loading = false,
  sortBy,
  sortDirection,
  onSort,
  onRowClick
}) => {
  if (loading) {
    return (
      <TableContainer>
        <LoadingContainer>
          <LoadingSpinner />
          <div>Loading data...</div>
        </LoadingContainer>
      </TableContainer>
    );
  }

  return (
    <TableContainer>
      <ScrollContainer>
        <StyledTable>
          <TableHeader>
            <tr>
              {columns.map(column => (
                <TableHeaderCell
                  key={column.key}
                  sortable={column.sortable}
                  onClick={column.sortable && onSort ? () => onSort(column.key) : undefined}
                >
                  {column.title}
                  {column.sortable && (
                    <SortIcon
                      direction={sortBy === column.key ? sortDirection : null}
                    />
                  )}
                </TableHeaderCell>
              ))}
            </tr>
          </TableHeader>
          <TableBody>
            {data.map((record, index) => (
              <TableRow
                key={record.id || index}
                clickable={!!onRowClick}
                onClick={onRowClick ? () => onRowClick(record) : undefined}
              >
                {columns.map(column => (
                  <TableCell key={column.key}>
                    {column.render ? column.render(record[column.key], record) : record[column.key]}
                  </TableCell>
                ))}
              </TableRow>
            ))}
          </TableBody>
        </StyledTable>
      </ScrollContainer>
      {data.length === 0 && !loading && (
        <EmptyState>
          📋 No data available
        </EmptyState>
      )}
    </TableContainer>
  );
};